import React from "react";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faCalendarAlt,
  faMoneyBillWave,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faPenToSquare,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";

const CATEGORY_LABEL = {
  branch: "Chi nhánh",
  department: "Phòng ban",
  personal: "Cá nhân",
};

const TYPE_LABEL = {
  finance: "Tài chính",
  work: "Công việc",
};

const ribbonByVariant = {
  waiting: {
    gradient: "from-amber-400 to-orange-500",
    fold: "border-t-amber-600",
    icon: faClock,
    defaultText: "Chờ duyệt",
  },
  approved: {
    gradient: "from-green-500 to-emerald-600",
    fold: "border-t-emerald-700",
    icon: faCheckCircle,
    defaultText: "Đã duyệt",
  },
  rejected: {
    gradient: "from-red-500 to-rose-600",
    fold: "border-t-rose-800",
    icon: faTimesCircle,
    defaultText: "Từ chối",
  },
  draft: {
    gradient: "from-gray-500 to-slate-600",
    fold: "border-t-slate-700",
    icon: faPenToSquare,
    defaultText: "Nháp",
  },
};

const PlanWorkflowCard = ({
  plan,
  variant = "waiting",
  currentUserId,
  onSelect,
  canApprove = false,
}) => {
  const cfg = ribbonByVariant[variant] || ribbonByVariant.waiting;
  const start = plan.start_date ? dayjs(plan.start_date) : null;
  const end = plan.end_date ? dayjs(plan.end_date) : null;
  const range =
    start?.isValid() && end?.isValid()
      ? `${start.format("DD/MM/YY")} → ${end.format("DD/MM/YY")}`
      : "—";

  const budget =
    plan.budget != null && plan.budget !== ""
      ? `${new Intl.NumberFormat("vi-VN").format(Number(plan.budget))} đ`
      : "—";

  const avatar = plan.avatar_url ? (
    <img
      src={plan.avatar_url}
      alt=""
      className="h-9 w-9 shrink-0 rounded-full object-cover ring-2 ring-white shadow"
    />
  ) : (
    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-200 ring-2 ring-white shadow">
      <FontAwesomeIcon icon={faUserCircle} className="text-xl text-gray-400" />
    </div>
  );

  const ribbonText =
    variant === "waiting" && canApprove ? "Cần bạn duyệt" : cfg.defaultText;

  return (
    <button
      type="button"
      onClick={() => onSelect?.(plan)}
      className="group relative flex w-full min-h-[168px] flex-col rounded-2xl border border-gray-100 bg-white p-4 text-left shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-red-300 hover:shadow-xl active:scale-[0.98]"
    >
      <div className="absolute right-0 top-0 z-10 flex flex-col items-end">
        <div
          className={`relative flex items-center gap-1.5 rounded-bl-xl bg-gradient-to-br px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white shadow-sm ${cfg.gradient} ${
            variant === "waiting" && canApprove ? "animate-pulse" : ""
          }`}
        >
          <div className="pointer-events-none absolute inset-0 rounded-bl-xl bg-black/5" />
          <FontAwesomeIcon icon={cfg.icon} className="relative text-[11px]" />
          <span className="relative">{ribbonText}</span>
          {variant === "waiting" && canApprove && (
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
          )}
        </div>
        <div
          className={`h-0 w-0 border-l-[6px] border-l-transparent border-t-[6px] ${cfg.fold}`}
        />
      </div>

      <div className="mb-3 flex min-w-0 items-start gap-3 pr-14 pt-1">
        {avatar}
        <div className="min-w-0 flex-1">
          <h4 className="truncate text-sm font-bold text-gray-900 transition-colors group-hover:text-red-600">
            {plan.title || "Không tiêu đề"}
          </h4>
          <p className="truncate text-[11px] font-medium uppercase tracking-tight text-gray-500">
            {plan.creator_name || "—"}
            {plan.creator_id && currentUserId && plan.creator_id === currentUserId ? (
              <span className="ml-1 font-bold text-red-600">(Bạn)</span>
            ) : null}
          </p>
        </div>
      </div>

      <p className="mb-3 line-clamp-2 flex-1 pl-1 text-[12px] leading-snug text-gray-600">
        {plan.content || "—"}
      </p>

      <div className="mt-auto flex flex-wrap gap-1 border-t border-gray-50 pt-3">
        <span className="rounded-md border border-gray-100 bg-gray-50 px-2 py-0.5 text-[9px] font-bold uppercase text-gray-600">
          {CATEGORY_LABEL[plan.plan_category] || plan.plan_category || "—"}
        </span>
        <span className="rounded-md border border-red-50 bg-red-50/80 px-2 py-0.5 text-[9px] font-bold uppercase text-red-700">
          {TYPE_LABEL[plan.plan_type] || plan.plan_type || "—"}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[10px] font-medium text-gray-400">
        <span className="flex min-w-0 items-center gap-1">
          <FontAwesomeIcon icon={faBuilding} className="shrink-0 text-[9px] text-gray-400" />
          <span className="truncate max-w-[10rem]">
            {plan.department_name || plan.branch_name || "—"}
          </span>
        </span>
        <span className="flex items-center gap-1">
          <FontAwesomeIcon icon={faMoneyBillWave} className="text-[9px]" />
          {budget}
        </span>
      </div>

      <div className="mt-1 flex items-center gap-1 text-[10px] text-gray-400">
        <FontAwesomeIcon icon={faCalendarAlt} className="text-[9px]" />
        {range}
      </div>
    </button>
  );
};

export default PlanWorkflowCard;
